/** Wiederverwendbare Bausteine. Farben/Abstände zentral hier – so bleibt das Design einheitlich. */
import { useEffect, useRef, useState, type ReactNode } from 'react';

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

export function Card({ children, className, onClick }: { children: ReactNode; className?: string; onClick?: () => void }) {
  const cls = cx('rounded-3xl border border-line bg-card p-4 shadow-[0_1px_0_rgba(0,0,0,0.03)]', onClick && 'active:scale-[0.99] transition-transform', className);
  if (onClick) return <button type="button" onClick={onClick} className={cx(cls, 'block w-full text-left')}>{children}</button>;
  return <div className={cls}>{children}</div>;
}

type Tone = 'moss' | 'water' | 'soil' | 'ghost' | 'alert';
const TONES: Record<Tone, string> = {
  moss: 'bg-moss text-paper',
  water: 'bg-water text-white',
  soil: 'bg-moss-soft text-moss',
  ghost: 'bg-transparent text-ink border border-line',
  alert: 'bg-alert text-white',
};

export function Button({
  children,
  onClick,
  tone = 'moss',
  className,
  disabled,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  tone?: Tone;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit';
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cx('min-h-12 rounded-2xl px-4 py-3 text-[15px] font-semibold transition active:scale-[0.97] disabled:opacity-40', TONES[tone], className)}
    >
      {children}
    </button>
  );
}

/** Panel von unten (wie ein iOS-Aktionsblatt). Schließt per Tipp daneben oder Wischen nach unten. */
export function Sheet({ open, onClose, title, children }: { open: boolean; onClose: () => void; title?: string; children: ReactNode }) {
  const [drag, setDrag] = useState(0);
  const startY = useRef<number | null>(null);
  useEffect(() => {
    if (!open) return;
    setDrag(0);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);
  if (!open) return null;
  // Wischen nur am Griff/Titel, damit Scrollen im Inhalt normal bleibt
  const onStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0]?.clientY ?? null;
  };
  const onMove = (e: React.TouchEvent) => {
    if (startY.current == null) return;
    const dy = (e.touches[0]?.clientY ?? startY.current) - startY.current;
    setDrag(Math.max(0, dy));
  };
  const onEnd = () => {
    startY.current = null;
    if (drag > 90) onClose();
    else setDrag(0);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" aria-label="Schließen" className="absolute inset-0 bg-black/35" onClick={onClose} />
      <div
        className="sheet-in pb-safe relative max-h-[88dvh] w-full max-w-lg overflow-y-auto rounded-t-[28px] bg-paper px-5"
        style={drag ? { transform: `translateY(${drag}px)`, transition: 'none' } : { transition: 'transform 0.2s ease-out' }}
      >
        <div className="-mx-5 touch-none px-5 pt-3" onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}>
          <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-line" />
          {title && <h2 className="font-display mb-3 text-2xl">{title}</h2>}
        </div>
        {children}
      </div>
    </div>
  );
}

export function Pill({ children, tone = 'soil', className }: { children: ReactNode; tone?: 'soil' | 'water' | 'sun' | 'alert' | 'moss'; className?: string }) {
  const t = {
    soil: 'bg-moss-soft text-moss',
    water: 'bg-water-soft text-water',
    sun: 'bg-sun-soft text-soil',
    alert: 'bg-alert-soft text-alert',
    moss: 'bg-moss text-paper',
  }[tone];
  return <span className={cx('inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold', t, className)}>{children}</span>;
}

export function SectionTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="mt-7 mb-2.5 flex items-baseline justify-between px-1">
      <h2 className="text-[13px] font-semibold tracking-wide text-muted uppercase">{children}</h2>
      {right}
    </div>
  );
}

/**
 * Ein-/Aus-Schalter im iOS-Stil. Safari (ab 17.4) zeichnet mit dem `switch`-Attribut
 * den echten iOS-Schalter; überall sonst sorgt die CSS-Klasse `.toggle` für dasselbe Aussehen.
 */
export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="flex min-h-12 items-center justify-between gap-3">
      <span className="text-[15px]">{label}</span>
      <input
        type="checkbox"
        role="switch"
        // `switch` = neues HTML-Attribut (Safari 17.4+) → echter iOS-Schalter
        {...{ switch: '' }}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="toggle shrink-0"
      />
    </label>
  );
}
